import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Target,
  TrendingUp,
  DollarSign,
  Users,
  Zap,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Play,
  Settings,
  ChevronRight
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { CompetitorMap } from '../../components/analytics/CompetitorMap';
// import { useCompetitorAnalysis } from '../../hooks/useCompetitorAnalysis';
import { Competitor, SimulationParameters, SimulationResult, CompetitorAnalysisResponse } from '../../types/competitor';

export default function CompetitorMapping() {
  const { propertyId } = useParams<{ propertyId: string }>();
  const [selectedCompetitor, setSelectedCompetitor] = useState<Competitor | null>(null);
  const [selectedProperty, setSelectedProperty] = useState(propertyId || 'demo-property-1');
  const [radius, setRadius] = useState(5);
  const [modelType, setModelType] = useState<'bertrand' | 'cournot' | 'stackelberg'>('bertrand');
  const [showSimulation, setShowSimulation] = useState(false);

  // Temporarily commented out competitor analysis to fix Netlify build issues
  // const { 
  //   analysis, 
  //   loading, 
  //   error, 
  //   refreshAnalysis, 
  //   runSimulation, 
  //   simulationLoading,
  //   simulationResults,
  //   isConnected,
  //   connectionStatus 
  // } = useCompetitorAnalysis({
  //   propertyId: selectedProperty,
  //   radius,
  //   includeSimulations: true,
  //   modelType,
  //   autoRefresh: true,
  //   refreshInterval: 5 // 5 minutes
  // });

  // Mock data for UI display while competitor analysis is disabled
  const analysis: CompetitorAnalysisResponse | null = null;
  const loading = false;
  const error: string | null = null;
  const refreshAnalysis = () => Promise.resolve();
  const runSimulation = (_scenarios: SimulationParameters[]) => Promise.resolve([] as SimulationResult[]);
  const simulationLoading = false;
  const simulationResults: SimulationResult[] = [];
  const isConnected = false;
  const connectionStatus = 'disconnected' as 'connecting' | 'connected' | 'disconnected' | 'error';

  const handleRunSimulation = async () => {
    const scenarios: SimulationParameters[] = [
      {
        subjectPriceChange: -0.05, // 5 cent decrease
        demandShift: 0,
        competitorReactionSpeed: 2, // 2 days
        marketConditions: 'normal'
      }
    ];
    
    await runSimulation(scenarios);
  };

  const getConnectionStatusColor = () => {
    const status = connectionStatus as 'connecting' | 'connected' | 'disconnected' | 'error';
    switch (status) {
      case 'connected': return 'text-green-500';
      case 'connecting': return 'text-yellow-500';
      case 'error': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getConnectionStatusIcon = () => {
    const status = connectionStatus as 'connecting' | 'connected' | 'disconnected' | 'error';
    switch (status) {
      case 'connected': return CheckCircle;
      case 'connecting': return RefreshCw;
      case 'error': return AlertTriangle;
      default: return Zap;
    }
  };

  // Mock analysis data for display when analysis is null
  const mockAnalysis: CompetitorAnalysisResponse = analysis || {
    subjectProperty: {
      id: selectedProperty,
      coordinates: { lat: 34.0522, lng: -118.2437 },
      currentMetrics: {
        fuelPrice: 4.25,
        monthlyVolume: 150000,
        cstoreSales: 45000,
        margin: 0.15,
        customerCount: 2500,
        trafficCount: 8500
      },
      brand: 'Shell',
      address: '123 Main St, Los Angeles, CA',
      name: 'Shell Station - Main St'
    },
    competitors: [
      {
        id: 'comp-1',
        name: 'Chevron - Sunset Blvd',
        brand: 'Chevron',
        address: '456 Sunset Blvd, Los Angeles, CA',
        coordinates: { lat: 34.0522, lng: -118.2337 },
        distance: 1.2,
        driveTime: 3,
        marketShare: 0.15,
        competitiveStrength: 0.75,
        metrics: {
          fuelPrice: 4.35,
          monthlyVolume: 120000,
          cstoreSales: 38000,
          margin: 0.12,
          customerCount: 2200,
          trafficCount: 7500
        }
      },
      {
        id: 'comp-2',
        name: 'Arco - Hollywood Blvd',
        brand: 'Arco',
        address: '789 Hollywood Blvd, Los Angeles, CA',
        coordinates: { lat: 34.0622, lng: -118.2537 },
        distance: 2.8,
        driveTime: 7,
        marketShare: 0.22,
        competitiveStrength: 0.85,
        metrics: {
          fuelPrice: 4.15,
          monthlyVolume: 180000,
          cstoreSales: 52000,
          margin: 0.18,
          customerCount: 3200,
          trafficCount: 9200
        }
      },
      {
        id: 'comp-3',
        name: '76 - Wilshire Blvd',
        brand: '76',
        address: '321 Wilshire Blvd, Los Angeles, CA',
        coordinates: { lat: 34.0422, lng: -118.2637 },
        distance: 4.1,
        driveTime: 12,
        marketShare: 0.08,
        competitiveStrength: 0.45,
        metrics: {
          fuelPrice: 4.28,
          monthlyVolume: 95000,
          cstoreSales: 28000,
          margin: 0.14,
          customerCount: 1800,
          trafficCount: 6200
        }
      },
      {
        id: 'comp-4',
        name: 'Mobil - Beverly Hills',
        brand: 'Mobil',
        address: '654 Beverly Dr, Beverly Hills, CA',
        coordinates: { lat: 34.0722, lng: -118.2437 },
        distance: 3.5,
        driveTime: 10,
        marketShare: 0.12,
        competitiveStrength: 0.35,
        metrics: {
          fuelPrice: 4.45,
          monthlyVolume: 75000,
          cstoreSales: 35000,
          margin: 0.16,
          customerCount: 1600,
          trafficCount: 5800
        }
      }
    ],
    gameTheoryResults: {
      nashEquilibrium: {
        optimalPricing: 0,
        expectedVolume: 0,
        expectedRevenue: 0,
        marketShare: 0
      },
      competitorResponses: [],
      sensitivityAnalysis: []
    },
    marketAnalysis: {
      totalMarketSize: 0,
      marketConcentration: 0,
      priceElasticity: 0,
      demandCurve: [],
      seasonalFactors: []
    },
    simulations: [],
    metadata: {
      analysisId: '',
      timestamp: new Date().toISOString(),
      radius: 0,
      competitorCount: 0,
      modelVersion: '1.0',
      confidence: 0
    }
  };

  const ConnectionStatusIcon = getConnectionStatusIcon();

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Analysis Error</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={refreshAnalysis}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry Analysis
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/dashboard" className="text-blue-600 hover:text-blue-800 font-medium">Dashboard</Link>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <span className="text-gray-500">Competitor Mapping</span>
          </div>
        </div>
      </div>

    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Competitor Mapping & Analysis</h1>
          <p className="text-sm text-gray-500 mt-1">
            Interactive geospatial analysis with game theory modeling
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <div className={`flex items-center gap-2 text-sm ${getConnectionStatusColor()}`}>
            <ConnectionStatusIcon className="w-4 h-4" />
            <span className="capitalize">{connectionStatus}</span>
          </div>
          <Button variant="outline" onClick={refreshAnalysis} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Controls */}
      <Card className="p-4">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Property</label>
              <Select
                value={selectedProperty}
                onChange={setSelectedProperty}
                options={[
                  { value: 'demo-property-1', label: 'Shell Station - Main St' },
                  { value: 'demo-property-2', label: 'Chevron - Broadway' },
                  { value: 'demo-property-3', label: 'Mobil - Highway 101' }
                ]}
                className="w-full sm:w-48"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Game Theory Model</label>
              <Select
                value={modelType}
                onChange={(value) => setModelType(value as 'bertrand' | 'cournot' | 'stackelberg')}
                options={[
                  { value: 'bertrand', label: 'Bertrand Competition' },
                  { value: 'cournot', label: 'Cournot Competition' },
                  { value: 'stackelberg', label: 'Stackelberg Leadership' }
                ]}
                className="w-full sm:w-48"
              />
            </div>
          </div>

          <Button 
            onClick={handleRunSimulation} 
            disabled={simulationLoading || !analysis}
            className="w-full sm:w-auto"
          >
            <Play className="w-4 h-4 mr-2" />
            {simulationLoading ? 'Running...' : 'Run Simulation'}
          </Button>
        </div>
      </Card>

      {/* Key Metrics */}
      {mockAnalysis && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Competitors</p>
                <p className="text-2xl font-bold">{mockAnalysis.competitors.length}</p>
              </div>
              <Target className="w-8 h-8 text-blue-500" />
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Market Share</p>
                <p className="text-2xl font-bold">
                  {(mockAnalysis.gameTheoryResults.nashEquilibrium.marketShare * 100).toFixed(1)}%
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500" />
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Optimal Price</p>
                <p className="text-2xl font-bold">
                  ${mockAnalysis.gameTheoryResults.nashEquilibrium.optimalPricing.toFixed(2)}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-yellow-500" />
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Expected Volume</p>
                <p className="text-2xl font-bold">
                  {(mockAnalysis.gameTheoryResults.nashEquilibrium.expectedVolume / 1000).toFixed(0)}K
                </p>
              </div>
              <Zap className="w-8 h-8 text-purple-500" />
            </div>
          </Card>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <div className="lg:col-span-2">
          <CompetitorMap
            analysis={mockAnalysis}
            loading={loading}
            onRadiusChange={setRadius}
            onCompetitorSelect={setSelectedCompetitor}
            selectedCompetitor={selectedCompetitor}
          />
        </div>

        {/* Competitor Details & Analysis */}
        <div className="space-y-4">
          {/* Selected Competitor */}
          {selectedCompetitor && (
            <Card className="p-4">
              <h3 className="font-semibold mb-3">Selected Competitor</h3>
              <div className="space-y-2">
                <div>
                  <p className="font-medium">{selectedCompetitor.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedCompetitor.address}</p>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Distance:</span>
                    <span className="ml-1 font-medium">{selectedCompetitor.distance.toFixed(1)} mi</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Drive Time:</span>
                    <span className="ml-1 font-medium">{selectedCompetitor.driveTime} min</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Price:</span>
                    <span className="ml-1 font-medium">${selectedCompetitor.metrics.fuelPrice.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Volume:</span>
                    <span className="ml-1 font-medium">{(selectedCompetitor.metrics.monthlyVolume / 1000).toFixed(0)}K</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Market Share:</span>
                    <span className="ml-1 font-medium">{(selectedCompetitor.marketShare * 100).toFixed(1)}%</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Threat Level:</span>
                    <span className={`ml-1 font-medium ${
                      selectedCompetitor.competitiveStrength >= 0.8 ? 'text-red-500' :
                      selectedCompetitor.competitiveStrength >= 0.6 ? 'text-yellow-500' : 'text-green-500'
                    }`}>
                      {(selectedCompetitor.competitiveStrength * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Game Theory Results */}
          {mockAnalysis.gameTheoryResults && (
            <Card className="p-4">
              <h3 className="font-semibold mb-3">Nash Equilibrium</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Optimal Pricing:</span>
                  <span className="font-medium">${mockAnalysis.gameTheoryResults.nashEquilibrium.optimalPricing.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Expected Volume:</span>
                  <span className="font-medium">{(mockAnalysis.gameTheoryResults.nashEquilibrium.expectedVolume / 1000).toFixed(0)}K</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Expected Revenue:</span>
                  <span className="font-medium">${(mockAnalysis.gameTheoryResults.nashEquilibrium.expectedRevenue / 1000).toFixed(0)}K</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Market Share:</span>
                  <span className="font-medium">{(mockAnalysis.gameTheoryResults.nashEquilibrium.marketShare * 100).toFixed(1)}%</span>
                </div>
              </div>
            </Card>
          )}

          {/* Market Analysis */}
          {mockAnalysis.marketAnalysis && (
            <Card className="p-4">
              <h3 className="font-semibold mb-3">Market Analysis</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Market Size:</span>
                  <span className="font-medium">{(mockAnalysis.marketAnalysis.totalMarketSize / 1000).toFixed(0)}K gal</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Market Concentration:</span>
                  <span className="font-medium">{mockAnalysis.marketAnalysis.marketConcentration.toFixed(3)} HHI</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Price Elasticity:</span>
                  <span className="font-medium">{mockAnalysis.marketAnalysis.priceElasticity.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Analysis Confidence:</span>
                  <span className="font-medium">{(mockAnalysis.metadata.confidence * 100).toFixed(0)}%</span>
                </div>
              </div>
            </Card>
          )}

          {/* Simulation Results */}
          {simulationResults.length > 0 && (
            <Card className="p-4">
              <h3 className="font-semibold mb-3">Simulation Results</h3>
              <div className="space-y-3">
                {simulationResults.map((result, index) => (
                  <div key={result.scenarioId} className="border rounded p-3">
                    <p className="font-medium text-sm mb-2">{result.scenarioName}</p>
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Volume Change:</span>
                        <span className={`font-medium ${
                          result.outcomes.cumulativeImpact.totalVolumeChange > 0 ? 'text-green-500' : 'text-red-500'
                        }`}>
                          {result.outcomes.cumulativeImpact.totalVolumeChange > 0 ? '+' : ''}
                          {(result.outcomes.cumulativeImpact.totalVolumeChange / 1000).toFixed(0)}K
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Revenue Impact:</span>
                        <span className={`font-medium ${
                          result.outcomes.cumulativeImpact.totalRevenueChange > 0 ? 'text-green-500' : 'text-red-500'
                        }`}>
                          {result.outcomes.cumulativeImpact.totalRevenueChange > 0 ? '+' : ''}
                          ${(result.outcomes.cumulativeImpact.totalRevenueChange / 1000).toFixed(0)}K
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">ROI:</span>
                        <span className="font-medium">{(result.outcomes.cumulativeImpact.roi * 100).toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
    </div>
  );
}
